import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { App, ProviderType, User } from "@/types";
import { Template } from "@/types/template";
import useSWR from "swr";
import { formatMarkdown } from "@/components/formatMarkdown";
import { getTagLabel, getTemplateSourceLabel } from "@/components/launch/ListTemplateCard";
import { ExternalLink, MarketingLink } from "@/components/OptionalLink";
import { useMemo } from "react";
import { TenantPlan } from "@/lib/plan";
import { Icon } from "@/components/landing/Navbar";

const reqSuffix = { memory: "MB", cpu: " vCPU", storage: "GB" } as const;

const TemplateForkButton = ({ url: repositoryUrl }: { url: string }) => {
  const { repositoryOwner, repositoryName } = useMemo(() => {
    const url = new URL(repositoryUrl);
    if (url.hostname !== "github.com") {
      return {};
    }

    const path = url.pathname.split("/");
    return {
      repositoryOwner: path[1],
      repositoryName: path[2]
    };
  }, [repositoryUrl]);

  return (
    <Button variant="outline" asChild>
      <ExternalLink
        href={`https://github.com/new?template_name=${repositoryName}&template_owner=${repositoryOwner}`}
      >
        Fork this template
      </ExternalLink>
    </Button>
  );
};

const TemplatePreviewButton = ({ demoUrl }: { demoUrl: string }) => {
  return (
    <Button asChild>
      <ExternalLink href={demoUrl} target="_blank" rel="noopener noreferrer">
        Live Demo
      </ExternalLink>
    </Button>
  );
};

export const TemplateCard = ({
  template,
  user,
  provider
}: {
  template: Template;
  user?: Pick<User, "id"> | null;
  provider?: ProviderType;
}) => {
  const { data: tenant } = useSWR<TenantPlan>(user?.id ? "/api/tenant/plan" : null);
  const subscribed = tenant && tenant.maxApps > 0;
  const { data: apps } = useSWR<App[]>(user?.id ? "/api/app" : null);

  return (
    <div className="container mx-auto p-6 mt-20">
      <Card className="w-full max-w-3xl mx-auto">
        <CardHeader className="flex flex-col items-center space-y-4">
          <div className="mt-4">
            <img src={template.logo} alt={template.name} className="h-12 rounded-md" />
          </div>
          <div className="text-center">
            <CardTitle className="text-2xl font-bold">{template.name}</CardTitle>
            <CardDescription className="mt-2">
              {formatMarkdown(template.intro)}
            </CardDescription>
          </div>
          <div>
            <Button asChild>
              <MarketingLink
                href={
                  !user || !tenant || !apps
                    ? `/auth/signIn?callbackUrl=/r/${template.id}${provider ? `?provider=${provider}` : ""}`
                    : !subscribed
                      ? "/pricing"
                      : apps.length + 1 <= tenant.maxApps
                        ? `/r/${template.id}/host${provider ? `?provider=${provider}` : ""}`
                        : "/pricing"
                }
                className={`plausible-event-name=DeployTemplateNow plausible-event-template=${template.id}`}
              >
                <Icon className="h-5 w-5" light />
                {!user || !tenant || !apps
                  ? "Sign In to Deploy"
                  : !subscribed
                    ? "Subscribe to Deploy"
                    : apps.length + 1 <= tenant.maxApps
                      ? "Deploy Now"
                      : "Upgrade to Deploy"}
              </MarketingLink>
            </Button>
          </div>
        </CardHeader>

        <CardContent className="flex flex-col gap-2">
          <div>
            <h3 className="text-lg font-semibold mb-2">Requirements</h3>
            <div className="flex flex-wrap gap-2">
              {template.requirements &&
                Object.entries(template.requirements).map(([key, value], index) => (
                  <Badge key={index} variant="secondary">
                    {value + reqSuffix[key as keyof Template["requirements"]]}
                  </Badge>
                ))}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">Tags</h3>
            <div className="inline-flex gap-2 flex-wrap">
              {template.experimental ? (
                <Badge variant="destructive">Experimental</Badge>
              ) : null}

              {template.source ? (
                <Badge variant="outline">
                  {getTemplateSourceLabel(template.source) || "Test"}
                </Badge>
              ) : null}

              {template.tags?.sort().map(tag => (
                <Badge
                  key={tag}
                  variant="outline"
                  className="hover:bg-primary hover:text-primary-foreground cursor-pointer"
                >
                  <Link href={`/r?tag=${tag}`}>{getTagLabel(tag)}</Link>
                </Badge>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">Source code</h3>
            <div className="flex flex-col gap-2 flex-wrap text-sm text-secondary-foreground">
              <ExternalLink
                href={
                  template.app.sourcePath
                    ? (template.app.repositoryUrl?.endsWith("/")
                        ? template.app.repositoryUrl
                        : template.app.repositoryUrl + "/") +
                      "tree/" +
                      (template.app.sourceBranch ?? "main") +
                      "/" +
                      (template.app.sourcePath?.startsWith("/")
                        ? template.app.sourcePath.slice(1)
                        : template.app.sourcePath)
                    : template.app.repositoryUrl
                }
                className="link text-sm text-secondary-foreground break-all"
              >
                {template.app.repositoryUrl}
              </ExternalLink>

              {template.tags?.includes("template") && (
                <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                  <div className="flex gap-2">
                    <TemplateForkButton url={template.app.repositoryUrl} />
                    {template.demoUrl && <TemplatePreviewButton demoUrl={template.demoUrl} />}
                  </div>
                  <span className="text-sm text-muted-foreground">
                    Build your new project from this template.
                  </span>
                </div>
              )}

              {template.demoUrl && !template.tags?.includes("template") && (
                <div className="flex flex-col gap-2">
                  <h3 className="text-lg font-semibold mb-2">Preview</h3>
                  <TemplatePreviewButton demoUrl={template.demoUrl} />
                </div>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">Deploy time (approx)</h3>
            <div className="text-sm text-secondary-foreground">{template.deployTime}</div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">About {template.name}</h3>
            <div>
              {formatMarkdown(
                template.description,
                undefined,
                undefined,
                true,
                "text-sm text-secondary-foreground"
              )}
            </div>
          </div>

          {template.introVideoUrl && (
            <div className="w-full">
              <h3 className="text-lg font-semibold mb-2">Introduction to {template.name}</h3>
              <iframe
                width="100%"
                height="415"
                src={template.introVideoUrl}
                title="Introduction to {template.name}"
              />
            </div>
          )}

          <div>
            <h3 className="text-lg font-semibold mb-2">About DollarDeploy</h3>
            <div className="text-sm text-secondary-foreground">
              DollarDeploy allows you to easily deploy and manage apps on your own VPS without
              the need for SSH access. Deploy {template.name} with just a few clicks and start
              building your solution today!
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
